import { useEffect, useState } from 'react';
import { ROUTES, useHashRoute } from './router.js';
import { Disclaimer, Logo } from './components.js';
import { useWorkspace } from './state.js';
import { Home } from './pages/Home.js';
import { Assistant } from './pages/Assistant.js';
import { Calculator } from './pages/Calculator.js';
import { Documents } from './pages/Documents.js';
import { Estimate } from './pages/Estimate.js';
import { Scenarios } from './pages/Scenarios.js';
import { Readiness } from './pages/Readiness.js';
import { Plan } from './pages/Plan.js';
import { ModelCard } from './pages/ModelCard.js';
import { Privacy } from './pages/Privacy.js';

type Theme = 'dark' | 'light';

function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>(() =>
    (document.documentElement.getAttribute('data-theme') as Theme) || 'dark');
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'dark' ? '#06080a' : '#f3f7f7');
    try { localStorage.setItem('tb-theme', theme); } catch { /* preference only */ }
  }, [theme]);
  return [theme, () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))];
}

function Page({ route }: { route: string }) {
  switch (route) {
    case '#/calculator': return <Calculator />;
    case '#/assistant': return <Assistant />;
    case '#/documents': return <Documents />;
    case '#/estimate': return <Estimate />;
    case '#/scenarios': return <Scenarios />;
    case '#/readiness': return <Readiness />;
    case '#/plan': return <Plan />;
    case '#/model': return <ModelCard />;
    case '#/privacy': return <Privacy />;
    default: return <Home />;
  }
}

export function App() {
  const route = useHashRoute();
  const [theme, toggleTheme] = useTheme();
  const { loaded, persistent, loadError } = useWorkspace();

  return (
    <>
      <a className="sr-only" href="#main">Skip to content</a>
      <header className="site-header">
        <div className="wrap">
          <a className="brand" href="#/">
            <Logo />
            <span>
              <span className="brand-name">Tyche Business</span>
              <span className="brand-sub">Financing Assistant</span>
            </span>
          </a>
          <nav className="nav" aria-label="Main">
            {ROUTES.map((r) => (
              <a key={r.path} href={r.path} aria-current={route === r.path ? 'page' : undefined}>{r.label}</a>
            ))}
          </nav>
          <button type="button" className="icon-btn" onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}>
            <span aria-hidden="true">{theme === 'dark' ? '☀' : '☾'}</span>
          </button>
        </div>
      </header>

      <main id="main" className="wrap">
        {loaded && !persistent && (
          <div className="callout warn no-print" role="status" style={{ marginBottom: 18 }}>
            This browser is not allowing local storage (often private browsing), so progress will be lost when you close the
            tab. Use <a href="#/privacy">export</a> to keep a copy.
          </div>
        )}
        {loadError && (
          <div className="callout bad no-print" role="alert" style={{ marginBottom: 18 }}>
            Your saved progress could not be opened: {loadError} You are starting fresh; nothing has been deleted.
          </div>
        )}
        {loaded ? <Page route={route} /> : <p className="muted" aria-live="polite">Opening your workspace…</p>}
      </main>

      <footer className="site-footer">
        <div className="wrap">
          <nav aria-label="About">
            <a href="#/model">How the estimate works</a>
            <a href="#/privacy">Your data and privacy</a>
            <a href="https://www.sba.gov/funding-programs/loans/lender-match" target="_blank" rel="noreferrer">SBA Lender Match</a>
          </nav>
          <Disclaimer compact />
          <p className="small faint">Everything you enter and upload stays in this browser; only messages to the advisor chat are sent, with your calculator results. Estimates are built from SBA's public 7(a) loan data.</p>
        </div>
      </footer>
    </>
  );
}
